import {CommentsCategory} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {SapStatusCode} from '@hconnect/common/types'
import {useUpmPlant, useUpmPlantTimezone} from '@hconnect/upm'
import {useHasPermission} from '@hproduce/permissions'
import moment from 'moment-timezone'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  DalogActionDetails,
  DalogActionMetaAnnotations,
  TaskCreateMaintenanceNotificationIcon,
  TaskDetailsCardLayout,
  TaskEditIcon,
  TaskMenuActions,
  type TaskMenuOption
} from '../../components'
import {DalogActionsCommentsQueryKeyPrefix, FEATURE_NAMESPACE} from '../../consts'
import {useCreateDalogActionMaintenanceNotification, useDalogAction} from '../../hooks'
import {HProduceTaskIdentifiers, HProduceTask} from '../../types'
import {CommentsContainer} from '../CommentsContainer'

import {DalogCreateMaintenanceNotificationDialog} from './DalogCreateMaintenanceNotificationDialog'
import {
  DalogSapMaintenanceNotificationDetails,
  hasMaintenanceNotificationCreationTimedOut
} from './DalogSapMaintenanceNotificationDetails'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onEditTaskClick: (taskId: string) => void
}

export const DalogActionDetailsContainer = ({task, onClose, onEditTaskClick}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const upmPlant = useUpmPlant({plantId: task.plantId})
  const {data, isLoading, isError, refetch, isRefetching} = useDalogAction({
    plantUpmId: upmPlant.upmId,
    externalId: task.externalId
  })
  const timezone = useUpmPlantTimezone(task.plantId)
  const hasPermission = useHasPermission({
    plantId: task.plantId,
    countryId: upmPlant?.country ?? ''
  })
  const canEditDalogAction = hasPermission('EDIT_PLANT_EVENTS')

  const handleEditClick = useCallback(() => {
    onEditTaskClick(task.id)
  }, [onEditTaskClick, task.id])

  const [maintenanceNotificationDialogShown, setMaintenanceNotificationDialogShown] =
    useState<boolean>(false)

  const [createMaintenanceNotificationFailed, setCreateMaintenanceNotificationFailed] =
    useState<boolean>(false)

  const [maintenanceNotificationCreated, setMaintenanceNotificationCreated] =
    useState<boolean>(false)

  const onSuccessCreateMaintenanceNotification = useCallback(() => {
    setCreateMaintenanceNotificationFailed(false)
    setMaintenanceNotificationCreated(true)
  }, [])

  const onErrorCreateMaintenanceNotification = useCallback(() => {
    setCreateMaintenanceNotificationFailed(true)
  }, [])

  const createDalogNotification = useCreateDalogActionMaintenanceNotification(
    {
      plantUpmId: upmPlant?.upmId,
      externalId: task.externalId
    },
    {
      onError: onErrorCreateMaintenanceNotification,
      onSuccess: onSuccessCreateMaintenanceNotification
    }
  )

  const menuActions = useMemo(() => {
    const actions: TaskMenuOption[] = []

    if (canEditDalogAction) {
      actions.push({
        key: 'edit',
        label: t('task.action.edit'),
        onClick: handleEditClick,
        dataTestId: 'task-details-edit-button',
        icon: <TaskEditIcon />
      })
    }

    const sapInfo = data?.sapInfo
    const isSapInSubmitState = sapInfo?.sapStatusCode === SapStatusCode.SUBMITTED
    const isAlreadySapConverted = !!sapInfo?.sapStatusCode && !isSapInSubmitState
    const isTimedOut =
      sapInfo && hasMaintenanceNotificationCreationTimedOut(sapInfo, moment.tz(timezone ?? 'UTC'))
    const isSendDisabled =
      isAlreadySapConverted ||
      isSapInSubmitState ||
      createDalogNotification.isPending ||
      createMaintenanceNotificationFailed ||
      maintenanceNotificationCreated

    if (!isAlreadySapConverted || isTimedOut) {
      actions.push({
        key: 'send-maintenance-notification',
        dataTestId: 'task-details-send-maintenance-notification',
        icon: <TaskCreateMaintenanceNotificationIcon />,
        label: t('task.action.sendMaintenanceNotification'),
        onClick: () => setMaintenanceNotificationDialogShown(true),
        disabled: isSendDisabled && !isTimedOut
      })
    }

    return actions
  }, [
    canEditDalogAction,
    t,
    handleEditClick,
    data?.sapInfo,
    timezone,
    createDalogNotification.isPending,
    createMaintenanceNotificationFailed,
    maintenanceNotificationCreated
  ])

  const hasMenuActions = menuActions.length > 0

  return (
    <DataContentWrapper
      data={data}
      showLoader={isLoading}
      isError={isError}
      retryFunction={refetch}
      isRefetching={isRefetching}
      noDataSettings={{
        noDataMessage: t('task.message.taskNotFound'),
        onActionClick: () => onClose()
      }}
    >
      {data && upmPlant && (
        <TaskDetailsCardLayout
          title={data.title}
          onClose={onClose}
          metaAnnotations={
            <DalogActionMetaAnnotations
              createdOn={data.createdOn}
              lastModified={data.lastModified}
              timezone={timezone}
            />
          }
          {...(hasMenuActions && {
            menuActions: <TaskMenuActions options={menuActions} />
          })}
        >
          {data.sapInfo && (
            <DalogSapMaintenanceNotificationDetails
              item={data.sapInfo}
              notificationFailed={createMaintenanceNotificationFailed}
              timezone={timezone}
            />
          )}
          <DalogActionDetails
            dalogAction={data}
            plantId={task.plantId}
            plantName={upmPlant.name}
            timezone={timezone}
          />
          <CommentsContainer
            plantId={task.plantId}
            commentCategoryInstanceId={data.id}
            queryKeyPrefix={DalogActionsCommentsQueryKeyPrefix}
            commentsCategory={CommentsCategory.DalogActions}
          />
          {maintenanceNotificationDialogShown && (
            <DalogCreateMaintenanceNotificationDialog
              event={data}
              plantId={task.plantId}
              showDialog={maintenanceNotificationDialogShown}
              setDialog={setMaintenanceNotificationDialogShown}
              createNotification={createDalogNotification}
            />
          )}
        </TaskDetailsCardLayout>
      )}
    </DataContentWrapper>
  )
}
